import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../constants"
import { getHeroImageSrcSet } from "../utils/heroImageUtils"
import {
  getReviewHeroLayout,
  getReviewHeroOverlayOpacity,
  getReviewHeroPrimaryImageUrl,
  getReviewHeroSectionBackgroundColor,
  getReviewHeroTextColor,
  reviewHeroHeaderSyncStyle
} from "../utils/reviewHeroLayoutShared"
import { getReviewHeroImage } from "../utils/reviewHeroUtils"
import { useOptionalWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

import ReviewHeroContent from "./ReviewHeroContent"
import ShapeBorder, { getBaseLayoutFromShapeLayout, getShapeTypeFromLayout, hasShapeBorder } from "./ShapeBorder"

const ReviewHeroImagePanelMobile = ({ imageUrl, className, style }) =>
  imageUrl ? (
    <div className={className} style={{ ...style, backgroundImage: `url(${imageUrl})` }} />
  ) : (
    <div className={twMerge(className, "flex items-center justify-center rounded-lg bg-gray-200")} style={style}>
      <span className="text-gray-500">No image selected</span>
    </div>
  )

const ReviewHeroSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const builderContext = useOptionalWebsiteBuilderV2()
  const pages =
    builderContext?.pages?.length > 0 || builderContext?.publishedPages?.length > 0
      ? builderContext?.pages || builderContext?.publishedPages || []
      : practice?.pages || []

  const settings = section?.settings || {}
  const layout = getReviewHeroLayout(section)
  const effectiveLayout = getBaseLayoutFromShapeLayout(layout)
  const shapeType =
    settings.shapeBorder && settings.shapeBorder !== "none" ? settings.shapeBorder : getShapeTypeFromLayout(layout)
  const showShapeBorder = (settings.shapeBorder && settings.shapeBorder !== "none") || hasShapeBorder(layout)
  const isHighlighted = highlightedField === `section-${section.id}`
  const overlayOpacity = getReviewHeroOverlayOpacity(settings)
  const primaryImageUrl = getReviewHeroPrimaryImageUrl(section)
  const sectionBackgroundColor = getReviewHeroSectionBackgroundColor(section, theme, { useHeroImage: false })
  const images = settings.images || []

  const renderReviewContent = ({
    hasBackgroundImage = false,
    contentLayout = layout,
    isInWhiteContainer = false,
    align = "center",
    useHeroImage = false
  } = {}) => (
    <ReviewHeroContent
      section={section}
      practice={practice}
      theme={theme}
      isMobile
      align={align}
      hasBackgroundImage={hasBackgroundImage}
      textColor={getReviewHeroTextColor({
        layout: contentLayout,
        hasBackgroundImage,
        theme,
        isInWhiteContainer,
        settings,
        useHeroImage
      })}
    />
  )

  switch (effectiveLayout) {
    case "image_left":
    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <AnimatedSection className="mx-auto w-full px-5 py-8">
            <div className="flex flex-col items-center gap-5">
              <ReviewHeroImagePanelMobile
                imageUrl={primaryImageUrl}
                className="h-[320px] w-full bg-cover bg-center"
                style={{ margin: "1em 0" }}
              />
              <div className="w-full flex-1">{renderReviewContent({ align: "start" })}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_left":
    case "half_image_right":
      return (
        <section
          className={twMerge("relative flex min-h-300 flex-col", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: sectionBackgroundColor }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <div className="relative h-[200px]">
            {primaryImageUrl && (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${primaryImageUrl})` }}
                />
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }}
                />
              </>
            )}
          </div>
          <div className="flex items-center justify-center p-4">
            <AnimatedSection className="w-full max-w-lg">{renderReviewContent({})}</AnimatedSection>
          </div>
        </section>
      )

    case "scattered_images": {
      const imageCount = images.length

      if (imageCount === 1) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-12",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: sectionBackgroundColor, minHeight: "300px" }}>
            {images[0]?.url && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${images[0].url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8">
              {renderReviewContent({ hasBackgroundImage: true, contentLayout: "full_image" })}
            </AnimatedSection>
          </section>
        )
      }

      if (imageCount === 2) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-6",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: sectionBackgroundColor, minHeight: "400px" }}>
            <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
            <div className="relative mx-auto w-full px-4">
              <div className="mb-4 flex flex-col gap-4">
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[0]?.url})` }}
                />
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[1]?.url})` }}
                />
              </div>
              <div
                className="relative z-10 w-full bg-white p-4 text-center"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                <AnimatedSection>{renderReviewContent({ isInWhiteContainer: true })}</AnimatedSection>
              </div>
            </div>
          </section>
        )
      }

      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center py-6",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: sectionBackgroundColor, minHeight: "400px" }}>
          <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
          <div className="relative mx-auto w-full px-4">
            <div className="mb-4 flex flex-col gap-4">
              {images.slice(0, 3).map((image, index) => (
                <div
                  key={index}
                  className="h-[150px] w-full rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    backgroundImage: `url(${image?.url})`,
                    transform: `rotate(${index % 2 === 0 ? "-2deg" : "1deg"})`
                  }}
                />
              ))}
            </div>
            <div
              className="relative z-10 w-full bg-white p-4 text-center"
              style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
              <AnimatedSection>{renderReviewContent({ isInWhiteContainer: true })}</AnimatedSection>
            </div>
          </div>
        </section>
      )
    }

    case "overlap_double_image": {
      if (images.length >= 2) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-6",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: sectionBackgroundColor, minHeight: "400px" }}>
            <style>{reviewHeroHeaderSyncStyle(sectionBackgroundColor)}</style>
            <div className="relative mx-auto w-full px-4">
              <div className="mb-4 flex flex-col gap-4">
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[0]?.url})` }}
                />
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[1]?.url})` }}
                />
              </div>
              <div
                className="relative z-20 w-full bg-white p-4 text-center"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                <AnimatedSection>{renderReviewContent({ isInWhiteContainer: true })}</AnimatedSection>
              </div>
            </div>
          </section>
        )
      }

      if (images.length === 1) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-12",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: theme?.colors?.primary || "#3B82F6", minHeight: "300px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${images[0].url})` }} />
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8">
              {renderReviewContent({ hasBackgroundImage: true, contentLayout: "full_image" })}
            </AnimatedSection>
          </section>
        )
      }

      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "300px" }}>
          <style>{reviewHeroHeaderSyncStyle(theme?.colors?.bgLight || "#FFFFFF")}</style>
          <AnimatedSection className="mx-auto max-w-4xl px-4 py-8">
            {renderReviewContent({ isInWhiteContainer: true })}
          </AnimatedSection>
        </section>
      )
    }

    case "contained_image":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center p-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "300px" }}>
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {primaryImageUrl && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${primaryImageUrl})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>
          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8">
            {renderReviewContent({ hasBackgroundImage: true, contentLayout: "contained_image" })}
          </AnimatedSection>
        </section>
      )

    case "full_image":
    default: {
      const { heroImageUrl, useHeroImage } = getReviewHeroImage({ section, practice, pages })
      const backgroundColor = useHeroImage
        ? "transparent"
        : getReviewHeroSectionBackgroundColor(section, theme, { useHeroImage: false })

      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center",
            useHeroImage ? "min-h-[420px] py-16" : "py-12",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor }}>
          {!useHeroImage && <style>{reviewHeroHeaderSyncStyle(backgroundColor)}</style>}
          {useHeroImage && heroImageUrl && (
            <>
              <img
                src={heroImageUrl}
                srcSet={getHeroImageSrcSet(heroImageUrl) || undefined}
                alt=""
                className="absolute inset-0 size-full object-cover object-center"
                loading="eager"
                fetchpriority="high"
              />
              <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            </>
          )}
          <div className="relative z-10 mx-auto max-w-5xl px-5 text-center">
            {renderReviewContent({
              hasBackgroundImage: useHeroImage,
              contentLayout: "full_image",
              useHeroImage
            })}
          </div>
          {useHeroImage && showShapeBorder && shapeType && (
            <div className="absolute inset-x-0 bottom-0 z-20">
              <ShapeBorder type={shapeType} color={theme?.colors?.bgLight || "#FFFFFF"} isMobile />
            </div>
          )}
        </section>
      )
    }
  }
}

export default ReviewHeroSectionMobile
